import React, { useRef } from 'react';
import {
  calcContractAmountsFromInclusive,
  formatContractAmountField,
  parseContractAmountField,
} from '../../utils/contractAmount.util';
import { formatVndInput } from '../../utils/vndFormat';

export interface ContractAmountFormSlice {
  valueBeforeVat: string;
  valueWithVat: string;
  vatPercent: string;
  totalDiscount: string;
  totalVat: string;
}

interface ContractAmountFieldsProps {
  values: ContractAmountFormSlice;
  onChange: (patch: Partial<ContractAmountFormSlice>) => void;
}

const VndField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
}> = ({ label, value, onChange, hint }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input
      type="text"
      inputMode="numeric"
      className="w-full border rounded px-3 py-2"
      value={value}
      onChange={(e) => onChange(formatVndInput(e.target.value))}
    />
    {hint ? <p className="text-xs text-gray-500 mt-1">{hint}</p> : null}
  </div>
);

export const ContractAmountFields: React.FC<ContractAmountFieldsProps> = ({ values, onChange }) => {
  const valueBeforeVatManualRef = useRef(false);

  const applyInclusiveCalc = (valueWithVatRaw: string, vatPercentRaw: string) => {
    const valueWithVat = parseContractAmountField(valueWithVatRaw);
    const vatPercent = Number(vatPercentRaw) || 0;
    if (valueWithVat <= 0) {
      onChange({
        valueWithVat: formatContractAmountField(valueWithVatRaw),
        totalVat: '',
        ...(valueBeforeVatManualRef.current ? {} : { valueBeforeVat: '' }),
      });
      return;
    }
    const { valueBeforeVat, totalVat } = calcContractAmountsFromInclusive(valueWithVat, vatPercent);
    onChange({
      valueWithVat: formatContractAmountField(valueWithVat),
      totalVat: formatContractAmountField(totalVat),
      ...(valueBeforeVatManualRef.current
        ? {}
        : { valueBeforeVat: formatContractAmountField(valueBeforeVat) }),
    });
  };

  const handleValueWithVatChange = (raw: string) => {
    valueBeforeVatManualRef.current = false;
    applyInclusiveCalc(raw, values.vatPercent);
  };

  const handleVatPercentChange = (raw: string) => {
    const cleaned = raw.replace(/[^\d.]/g, '');
    valueBeforeVatManualRef.current = false;
    onChange({ vatPercent: cleaned });
    applyInclusiveCalc(values.valueWithVat, cleaned);
  };

  const handleValueBeforeVatChange = (raw: string) => {
    valueBeforeVatManualRef.current = true;
    const beforeVat = parseContractAmountField(raw);
    const withVat = parseContractAmountField(values.valueWithVat);
    onChange({
      valueBeforeVat: formatContractAmountField(raw),
      totalVat:
        withVat > 0 && beforeVat >= 0
          ? formatContractAmountField(Math.max(0, withVat - beforeVat))
          : values.totalVat,
    });
  };

  const handleTotalVatChange = (raw: string) => {
    onChange({ totalVat: formatContractAmountField(raw) });
  };

  const handleTotalDiscountChange = (raw: string) => {
    onChange({ totalDiscount: formatContractAmountField(raw) });
  };

  return (
    <>
      <VndField
        label="Giá trị HĐ (có VAT)"
        value={values.valueWithVat}
        onChange={handleValueWithVatChange}
      />
      <div>
        <label className="block text-sm font-medium mb-1">Thuế (%)</label>
        <input
          type="text"
          inputMode="decimal"
          className="w-full border rounded px-3 py-2"
          value={values.vatPercent}
          onChange={(e) => handleVatPercentChange(e.target.value)}
        />
      </div>
      <VndField
        label="Giá trị HĐ (chưa VAT)"
        value={values.valueBeforeVat}
        onChange={handleValueBeforeVatChange}
        hint="Tự động tính từ giá trị có VAT; có thể sửa thủ công khi cần"
      />
      <VndField
        label="Tổng giảm giá"
        value={values.totalDiscount}
        onChange={handleTotalDiscountChange}
      />
      <VndField
        label="Tổng VAT"
        value={values.totalVat}
        onChange={handleTotalVatChange}
        hint="Tự động tính khi nhập giá trị có VAT và thuế %"
      />
    </>
  );
};

export default ContractAmountFields;
